/**
 * Loads the fleet's data for the dashboard and keeps it in memory.
 *
 * The data comes in tiers. `core` holds every client's daily totals and is
 * all the first screen needs. Per-person, per-agent, per-service and list
 * data sit in further tier files, each fetched the first time a view asks
 * for it and merged into the snapshots already on screen. Nothing is cut in
 * any tier: a client's fragments from every tier join back into its whole
 * snapshot.
 *
 * Before the index's first build the bucket has no tier files. The hook then
 * reads the old per-client rows from DynamoDB, which hold everything in one
 * piece (with the detailed history cut to 92 days), so every tier reports
 * ready at once.
 *
 * The hook only reads. Nothing here writes to AWS.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fleetAnalyticsService } from '@/services/fleetAnalyticsService';
import type { ClientSnapshot, FleetIndexFile, PortalMeta } from '@/types/fleetAnalytics';
import { STALE_AFTER_HOURS } from './fleetData';
import type { ClientStatus, FleetData, FleetTier, Freshness, TierState } from './fleetData';
import {
  addDays,
  ageHours,
  ALL_TIERS,
  asOfDay,
  dropClaimedInfraRows,
  INFRA_CLIENT_PREFIX,
  mergeTierFragment,
  withDefaults,
} from './shared';

/** What the hook gives beyond the `FleetData` contract. The page uses these; a tab does not need them. */
export interface FleetDataEx extends FleetData {
  /** True when a client's complete snapshot could not be loaded. Its view then runs on the tier files. */
  fullFailed: (clientName: string) => boolean;
  /** Gzip bytes of each tier file, for the loading words. Index only. */
  tierBytes: Partial<Record<'core' | FleetTier, number>>;
  /** Which snapshot paths live in which tier, from the index. Undefined when nothing is split off. */
  tierPaths: PortalMeta['tier_paths'];
  /**
   * Clients the nightly update knows about that have no snapshot at all: the
   * update has never worked for them. They are in no view's numbers.
   */
  clientsWithNoData: string[];
}

const IDLE_TIERS: Record<FleetTier, TierState> = {
  users: 'idle',
  agents: 'idle',
  services: 'idle',
  inventory: 'idle',
  apps: 'idle',
  conversations: 'idle',
};
const READY_TIERS: Record<FleetTier, TierState> = {
  users: 'ready',
  agents: 'ready',
  services: 'ready',
  inventory: 'ready',
  apps: 'ready',
  conversations: 'ready',
};

/** A tier fetched while a nightly build replaced the index is from a different build than core. Reload this many times at most. */
const MAX_BUILD_MISMATCH_RELOADS = 2;

interface LoadedState {
  loading: boolean;
  error: string | null;
  source: 'index' | 'dynamodb' | null;
  indexGeneratedAt?: string;
  snapshots: ClientSnapshot[];
  status: Record<string, ClientStatus>;
  tiers: Record<FleetTier, TierState>;
  tierBytes: Partial<Record<'core' | FleetTier, number>>;
  tierPaths: PortalMeta['tier_paths'];
}

const EMPTY: LoadedState = {
  loading: true,
  error: null,
  source: null,
  snapshots: [],
  status: {},
  tiers: IDLE_TIERS,
  tierBytes: {},
  tierPaths: undefined,
};

/** A snapshot row, as opposed to a summed row an old rollup stored (`_FLEET`, `_CLIENTS`). */
const isSnapshotName = (name: string) => !name.startsWith('_') || name.startsWith(INFRA_CLIENT_PREFIX);

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function useFleetData(): FleetDataEx {
  const [state, setState] = useState<LoadedState>(EMPTY);
  const [full, setFull] = useState<Record<string, ClientSnapshot>>({});
  const [fullState, setFullState] = useState<Record<string, 'loading' | 'failed'>>({});

  // What is merged so far, before defaults are filled in. Tier fragments are
  // merged into these, never into the copies on screen.
  const rawRef = useRef(new Map<string, ClientSnapshot>());
  const loadedRef = useRef(new Set<FleetTier>());
  const wantedRef = useRef(new Set<FleetTier>());
  const inflightRef = useRef(new Set<FleetTier>());
  const failedTiersRef = useRef(new Set<FleetTier>());
  const sourceRef = useRef<LoadedState['source']>(null);
  const coreBuildRef = useRef<string | undefined>(undefined);
  const tierPathsRef = useRef<PortalMeta['tier_paths']>(undefined);
  const mismatchReloadsRef = useRef(0);
  const fullAskedRef = useRef(new Set<string>());
  // Each load is one generation. An answer from an older generation is dropped.
  const generationRef = useRef(0);
  // A tier load starts a reload when it meets a file from another build, and a
  // load starts tier loads: the ref breaks the circle.
  const reloadRef = useRef<() => void>(() => {});

  /** Put the merged snapshots on screen: defaults filled in, and each stamped with what is loaded. */
  const publish = useCallback((patch: Partial<LoadedState>) => {
    const tiers = sourceRef.current === 'dynamodb' ? ALL_TIERS : Array.from(loadedRef.current);
    // One object for every snapshot of this load: they all hold the same tiers.
    const shared: PortalMeta = { tiers, tier_paths: tierPathsRef.current };
    const snapshots = dropClaimedInfraRows(
      Array.from(rawRef.current.values()).map((raw) => {
        // An old DynamoDB row keeps the detailed history for `detail_days` only.
        const days = raw.header?.detail_days;
        const portal =
          sourceRef.current === 'dynamodb' && days ? { ...shared, detail_from: addDays(asOfDay(raw), -days) } : shared;
        return { ...withDefaults(raw), portal };
      })
    );
    setState((prev) => ({ ...prev, ...patch, snapshots }));
  }, []);

  const startTier = useCallback(
    (tier: FleetTier) => {
      if (loadedRef.current.has(tier) || inflightRef.current.has(tier) || failedTiersRef.current.has(tier)) return;
      const generation = generationRef.current;
      inflightRef.current.add(tier);
      // Called from a render (NeedsTier, the page), so the state change waits a tick.
      queueMicrotask(() => {
        if (generation !== generationRef.current) return;
        setState((prev) => ({ ...prev, tiers: { ...prev.tiers, [tier]: 'loading' } }));
      });
      void fleetAnalyticsService
        .fetchFleetTier(tier)
        .then((file) => {
          if (generation !== generationRef.current) return;
          inflightRef.current.delete(tier);
          if (!file) throw new Error('the file is not in the index');
          if (file.generated_at !== coreBuildRef.current) {
            // A nightly build landed between the core fetch and this one.
            // Two builds must never be merged: start again from core.
            if (mismatchReloadsRef.current < MAX_BUILD_MISMATCH_RELOADS) {
              mismatchReloadsRef.current += 1;
              reloadRef.current();
              return;
            }
            throw new Error('the file is from a different nightly build than the core data');
          }
          mismatchReloadsRef.current = 0;
          for (const [name, fragment] of Object.entries(file.clients)) {
            const base = rawRef.current.get(name);
            if (base) rawRef.current.set(name, mergeTierFragment(base, fragment));
          }
          loadedRef.current.add(tier);
          publish({});
          setState((prev) => ({ ...prev, tiers: { ...prev.tiers, [tier]: 'ready' } }));
        })
        .catch((e) => {
          if (generation !== generationRef.current) return;
          inflightRef.current.delete(tier);
          failedTiersRef.current.add(tier);
          console.error(`[fleet] the ${tier} tier did not load`, e);
          setState((prev) => ({ ...prev, tiers: { ...prev.tiers, [tier]: 'error' } }));
        });
    },
    [publish]
  );

  const applyIndex = useCallback(
    (core: FleetIndexFile) => {
      sourceRef.current = 'index';
      coreBuildRef.current = core.generated_at;
      tierPathsRef.current = core.tier_paths;
      rawRef.current = new Map();
      for (const [name, fragment] of Object.entries(core.clients)) {
        if (isSnapshotName(name)) rawRef.current.set(name, { ...fragment, client: name } as ClientSnapshot);
      }
      const status: Record<string, ClientStatus> = {};
      for (const [name, st] of Object.entries(core.status || {})) {
        status[name] = {
          ok: Boolean(st.ok),
          lastAttemptAt: st.last_attempt_at,
          lastOkAt: st.last_ok_at,
          lastError: st.last_error || undefined,
        };
      }
      publish({
        loading: false,
        error: null,
        source: 'index',
        indexGeneratedAt: core.generated_at,
        status,
        tiers: IDLE_TIERS,
        tierBytes: core.tier_bytes || {},
        tierPaths: core.tier_paths,
      });
      // Tiers a view asked for while core was still on its way.
      for (const tier of wantedRef.current) startTier(tier);
    },
    [publish, startTier]
  );

  const applyDynamo = useCallback(
    (rows: Record<string, ClientSnapshot>) => {
      sourceRef.current = 'dynamodb';
      coreBuildRef.current = undefined;
      tierPathsRef.current = undefined;
      rawRef.current = new Map();
      for (const [name, body] of Object.entries(rows)) {
        if (isSnapshotName(name)) rawRef.current.set(name, { ...body, client: body.client || name });
      }
      publish({
        loading: false,
        error: null,
        source: 'dynamodb',
        indexGeneratedAt: undefined,
        status: {},
        tiers: READY_TIERS,
        tierBytes: {},
        tierPaths: undefined,
      });
    },
    [publish]
  );

  const load = useCallback(async () => {
    const generation = ++generationRef.current;
    sourceRef.current = null;
    loadedRef.current = new Set();
    inflightRef.current = new Set();
    failedTiersRef.current = new Set();
    fullAskedRef.current = new Set();
    setFull({});
    setFullState({});
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const core = await fleetAnalyticsService.fetchFleetTier('core');
      if (generation !== generationRef.current) return;
      if (core) {
        applyIndex(core);
        return;
      }
      // No index in the bucket yet: read the old per-client rows.
      const rows = await fleetAnalyticsService.fetchAllSnapshots();
      if (generation !== generationRef.current) return;
      applyDynamo(rows);
    } catch (e) {
      if (generation !== generationRef.current) return;
      setState((prev) => ({ ...prev, loading: false, error: `The fleet data did not load: ${message(e)}` }));
    }
  }, [applyIndex, applyDynamo]);

  useEffect(() => {
    reloadRef.current = () => void load();
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(() => {
    mismatchReloadsRef.current = 0;
    void load();
  }, [load]);

  const ensureTier = useCallback(
    (tier: FleetTier) => {
      if (sourceRef.current === 'dynamodb') return;
      wantedRef.current.add(tier);
      // Core still on its way: `applyIndex` starts the tier when it lands.
      if (sourceRef.current === 'index') startTier(tier);
    },
    [startTier]
  );

  const hasTiers = useCallback(
    (...tiers: FleetTier[]) => tiers.every((t) => state.tiers[t] === 'ready'),
    [state.tiers]
  );

  const byName = useMemo(() => {
    const map = new Map<string, ClientSnapshot>();
    for (const s of state.snapshots) map.set(s.client, s);
    return map;
  }, [state.snapshots]);

  const ensureFull = useCallback((clientName: string) => {
    if (!sourceRef.current || fullAskedRef.current.has(clientName)) return;
    fullAskedRef.current.add(clientName);
    const generation = generationRef.current;
    const raw = rawRef.current.get(clientName);
    queueMicrotask(() => {
      if (generation !== generationRef.current) return;
      setFullState((prev) => ({ ...prev, [clientName]: 'loading' }));
    });
    void fleetAnalyticsService
      .fetchFullSnapshot(clientName, raw?.header?.s3_key)
      .then((body) => {
        if (generation !== generationRef.current) return;
        if (!body) throw new Error('no snapshot is stored for this client');
        const snapshot: ClientSnapshot = {
          ...withDefaults({ ...body, client: body.client || clientName }),
          portal: { tiers: ALL_TIERS, full: true },
        };
        setFull((prev) => ({ ...prev, [clientName]: snapshot }));
        setFullState((prev) => {
          const next = { ...prev };
          delete next[clientName];
          return next;
        });
      })
      .catch((e) => {
        if (generation !== generationRef.current) return;
        console.warn(`[fleet] the full snapshot of ${clientName} did not load`, e);
        setFullState((prev) => ({ ...prev, [clientName]: 'failed' }));
      });
  }, []);

  const fullSnapshot = useCallback((clientName: string) => full[clientName], [full]);
  const fullLoading = useCallback((clientName: string) => fullState[clientName] === 'loading', [fullState]);
  const fullFailed = useCallback((clientName: string) => fullState[clientName] === 'failed', [fullState]);

  const freshness = useCallback(
    (clientName: string): Freshness => {
      const st = state.status[clientName];
      // The freshest time on hand: a complete snapshot can be newer than the index.
      const times = [full[clientName]?.generated_at, byName.get(clientName)?.generated_at].filter((t): t is string =>
        Boolean(t)
      );
      const generatedAt = times.sort().pop() ?? st?.lastOkAt;
      const age = ageHours(generatedAt);
      // An infrastructure account has no status entry: its freshness is its snapshot's own time.
      const failed = st ? !st.ok : false;
      return {
        generatedAt,
        ageHours: age,
        stale: age !== null && age > STALE_AFTER_HOURS,
        failed,
        error: failed ? st?.lastError || undefined : undefined,
      };
    },
    [state.status, full, byName]
  );

  const staleAmong = useCallback(
    (clientNames: string[]) =>
      clientNames
        .map((clientName) => ({ clientName, ...freshness(clientName) }))
        .filter((f) => f.stale || f.failed)
        // Oldest first. A client with no data at all is older than any.
        .sort((a, b) => (b.ageHours ?? Infinity) - (a.ageHours ?? Infinity)),
    [freshness]
  );

  const clientsWithNoData = useMemo(
    () =>
      Object.keys(state.status)
        .filter((name) => !byName.has(name))
        .sort(),
    [state.status, byName]
  );

  return useMemo(
    () => ({
      loading: state.loading,
      error: state.error,
      source: state.source,
      indexGeneratedAt: state.indexGeneratedAt,
      snapshots: state.snapshots,
      status: state.status,
      freshness,
      staleAmong,
      tiers: state.tiers,
      ensureTier,
      hasTiers,
      fullSnapshot,
      ensureFull,
      fullLoading,
      fullFailed,
      reload,
      tierBytes: state.tierBytes,
      tierPaths: state.tierPaths,
      clientsWithNoData,
    }),
    [
      state,
      freshness,
      staleAmong,
      ensureTier,
      hasTiers,
      fullSnapshot,
      ensureFull,
      fullLoading,
      fullFailed,
      reload,
      clientsWithNoData,
    ]
  );
}

/**
 * The fleet's data as one stack's view sees it.
 *
 * A single-stack page draws from that stack's complete snapshot, which holds
 * every tier's data for the stack in about 1 MB. So for that page "is the
 * users tier loaded?" means "is the complete snapshot loaded?", and asking
 * for a tier loads the snapshot, not a file of several MB for the whole
 * fleet. `<NeedsTier>` works unchanged on either.
 *
 * When the complete snapshot cannot be had, the stack's view runs on the tier
 * files like any other.
 */
export function scopeFleetToStack(fleet: FleetDataEx, clientName: string): FleetData {
  if (fleet.source !== 'index' || fleet.fullFailed(clientName)) return fleet;
  const ready = fleet.fullSnapshot(clientName) !== undefined;
  const tierState: TierState = ready ? 'ready' : 'loading';
  return {
    ...fleet,
    tiers: {
      users: tierState,
      agents: tierState,
      services: tierState,
      inventory: tierState,
      apps: tierState,
      conversations: tierState,
    },
    ensureTier: () => fleet.ensureFull(clientName),
    hasTiers: () => ready,
  };
}
